import React, { useId } from 'react'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  getMobileExpandableCardSx,
  getMobileExpandableCardContentSx,
  getMobileExpandableCodeTriggerSx,
  getMobileExpandableDetailAvatarSx,
  getMobileExpandableDetailLabelSx,
  getMobileExpandableDetailRowSx,
  getMobileExpandableFooterWrapSx,
  MOBILE_EXPANDABLE_DETAIL_CONTENT_SX,
  MOBILE_EXPANDABLE_EXPAND_BUTTON_SX,
  MOBILE_EXPANDABLE_SUMMARY_ROW_SX,
  getMobileExpandableCodeTextSx,
  getMobileExpandableDetailValueSx,
  getMobileExpandableSummaryLabelSx,
  getMobileExpandableSummaryValueSx
} from './styles/mobileExpandableDetailCardSx'

const defaultAccent = '#597fa9'

function SummaryRow({
  label,
  children,
  valueTitle,
  accentColor,
  alignLabelAndValue,
  summaryLabelFontSize,
  summaryValueFontSize,
  valueColor,
  valueSx
}) {
  const resolvedValueSx = {
    ...getMobileExpandableSummaryValueSx(accentColor, summaryValueFontSize, valueColor),
    ...(valueSx || {})
  }

  return (
    <Box
      sx={{
        ...MOBILE_EXPANDABLE_SUMMARY_ROW_SX,
        ...(alignLabelAndValue
          ? {
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              alignItems: 'center',
              columnGap: 0.75,
              rowGap: 0.35
            }
          : {})
      }}
    >
      <Typography
        component="span"
        variant="caption"
        color="text.secondary"
        sx={getMobileExpandableSummaryLabelSx(summaryLabelFontSize)}
      >
        {label}:{' '}
      </Typography>
      {typeof children === 'string' || typeof children === 'number' ? (
        <Typography
          component="span"
          variant="body2"
          fontWeight={700}
          title={valueTitle}
          sx={resolvedValueSx}
        >
          {children}
        </Typography>
      ) : (
        <Box component="span" title={valueTitle} sx={resolvedValueSx}>
          {children}
        </Box>
      )}
    </Box>
  )
}

function DetailRow({
  icon,
  label,
  children,
  boldValue,
  valueTitle,
  accentColor,
  lighterDetailDividers,
  fullWidthFooter,
  alignLabelAndValue,
  detailLabelFontSize,
  detailValueFontSize,
  valueColor,
  valueSx,
  dividerColor
}) {
  const rowSx = (theme) =>
    getMobileExpandableDetailRowSx(theme, {
      lighterDivider: lighterDetailDividers,
      dividerColor
    })
  const hasIcon = icon !== null && icon !== undefined

  return (
    <Stack direction="column" spacing={fullWidthFooter ? 1 : 0} sx={rowSx}>
      <Stack
        direction="row"
        spacing={hasIcon ? 1.5 : 0}
        alignItems="flex-start"
        sx={{ width: '100%', minWidth: 0 }}
      >
        {hasIcon ? <Avatar sx={getMobileExpandableDetailAvatarSx}>{icon}</Avatar> : null}
        <Box
          sx={{
            ...MOBILE_EXPANDABLE_DETAIL_CONTENT_SX,
            ...(alignLabelAndValue
              ? {
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  columnGap: 0.75,
                  rowGap: 0.35
                }
              : {})
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            display={alignLabelAndValue ? 'inline-flex' : 'block'}
            sx={getMobileExpandableDetailLabelSx(detailLabelFontSize)}
          >
            {label}
            {alignLabelAndValue ? ':' : null}
          </Typography>
          {typeof children === 'string' || typeof children === 'number' ? (
            <Typography
              variant="body2"
              fontWeight={boldValue ? 700 : 500}
              title={valueTitle}
              sx={{
                ...getMobileExpandableDetailValueSx(
                  accentColor,
                  detailValueFontSize,
                  valueColor
                ),
                ...(valueSx || {})
              }}
            >
              {children}
            </Typography>
          ) : (
            children
          )}
        </Box>
      </Stack>
      {fullWidthFooter ? (
        <Box sx={{ width: '100%', minWidth: 0, pl: 0 }}>{fullWidthFooter}</Box>
      ) : null}
    </Stack>
  )
}

export default function MobileExpandableDetailCard({
  code,
  onCodeClick,
  expanded,
  onToggleExpand,
  summaryRows = [],
  detailRows = [],
  accentColor = defaultAccent,
  codeAccentColor,
  lighterDetailDividers = false,
  detailDividerColor,
  expandAriaLabel,
  detailRegionAriaLabel,
  footer = null,
  footerContent = null,
  footerDivider = false,
  footerDividerColor,
  footerPaddingTop = 1,
  showExpandControl = true,
  bodyFontSize = 16,
  codeFontSize = 16,
  summaryLabelFontSize = 11.52,
  summaryValueFontSize = 16,
  detailLabelFontSize = 11.2,
  detailValueFontSize = 14
}) {
  const resolvedCodeColor = codeAccentColor ?? accentColor
  const detailId = useId()
  const ariaExpand = expandAriaLabel || (expanded ? 'Ocultar detalle' : 'Mostrar detalle')
  const ariaRegion = detailRegionAriaLabel || `Detalle ${code}`
  const resolvedFooter = footerContent ?? footer

  return (
    <Card elevation={0} sx={getMobileExpandableCardSx}>
      <CardContent sx={getMobileExpandableCardContentSx({ bodyFontSize })}>
        <Stack
          direction="row"
          justifyContent={showExpandControl ? 'space-between' : 'flex-start'}
          alignItems="flex-start"
          spacing={1}
        >
          <Box
            component={onCodeClick ? 'button' : 'span'}
            type={onCodeClick ? 'button' : undefined}
            onClick={onCodeClick}
            sx={getMobileExpandableCodeTriggerSx(onCodeClick)}
          >
            <Typography
              variant="subtitle1"
              component="span"
              fontWeight={600}
              sx={getMobileExpandableCodeTextSx(resolvedCodeColor, codeFontSize)}
            >
              {code}
            </Typography>
          </Box>
          {showExpandControl ? (
            <IconButton
              size="small"
              aria-expanded={expanded}
              aria-controls={detailId}
              aria-label={ariaExpand}
              onClick={(e) => {
                e.stopPropagation()
                onToggleExpand()
              }}
              sx={MOBILE_EXPANDABLE_EXPAND_BUTTON_SX(accentColor)}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          ) : null}
        </Stack>

        <Box sx={{ pt: 1.25, pb: 0.5 }}>
          {summaryRows.map((row) => (
            <SummaryRow
              key={row.label}
              label={row.label}
              valueTitle={row.valueTitle}
              accentColor={accentColor}
              alignLabelAndValue={row.alignLabelAndValue}
              summaryLabelFontSize={summaryLabelFontSize}
              summaryValueFontSize={summaryValueFontSize}
              valueColor={row.valueColor}
              valueSx={row.valueSx}
            >
              {row.value}
            </SummaryRow>
          ))}
        </Box>

        <Collapse in={expanded} timeout="auto">
          <Box id={detailId} role="region" aria-label={ariaRegion} sx={{ pt: 0.5, pb: 0.25 }}>
            {detailRows.map((row, index) => (
              <DetailRow
                key={row.detailRowKey ?? `${row.label}-${index}`}
                icon={row.icon}
                label={row.label}
                boldValue={row.boldValue}
                valueTitle={row.valueTitle}
                accentColor={accentColor}
                lighterDetailDividers={lighterDetailDividers}
                fullWidthFooter={row.fullWidthFooter}
                alignLabelAndValue={row.alignLabelAndValue}
                detailLabelFontSize={detailLabelFontSize}
                detailValueFontSize={detailValueFontSize}
                valueColor={row.valueColor}
                valueSx={row.valueSx}
                dividerColor={row.dividerColor || detailDividerColor}
              >
                {row.value}
              </DetailRow>
            ))}
          </Box>
        </Collapse>

        {resolvedFooter ? (
          <Box
            sx={getMobileExpandableFooterWrapSx({
              footerDivider,
              footerDividerColor,
              footerPaddingTop
            })}
          >
            {resolvedFooter}
          </Box>
        ) : null}
      </CardContent>
    </Card>
  )
}
